import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Download, Search, Star, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const monthAgo = () => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export const downloadCsv = async (url, params, name) => {
  try {
    const { data } = await api.get(url, { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { toast.error("Falha ao exportar"); }
};

export const useReportFilters = () => {
  const [f, setF] = useState({ start: monthAgo(), end: todayIso(), user_id: "", phone: "", q: "" });
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users/directory").then(({ data }) => setUsers(data)).catch(() => {}); }, []);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  return { f, setF, users, params };
};

export const FiltersBar = ({ f, setF, users, onSearch, contact = true, children }) => (
  <Card><CardContent className="p-4 flex flex-wrap items-end gap-3">
    <div><Label className="text-xs">De</Label><Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-9 mt-1" data-testid="report-start" /></div>
    <div><Label className="text-xs">Até</Label><Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="h-9 mt-1" data-testid="report-end" /></div>
    <div>
      <Label className="text-xs">Atendente</Label>
      <select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} className="h-9 mt-1 w-44 border border-border rounded-md bg-background px-2 text-sm block" data-testid="report-user">
        <option value="">Todos</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
    </div>
    {contact && <div><Label className="text-xs">Contato (nome)</Label><Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="h-9 mt-1 w-40" data-testid="report-contact" /></div>}
    {contact && <div><Label className="text-xs">Telefone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-9 mt-1 w-36" data-testid="report-phone" /></div>}
    <Button size="sm" onClick={onSearch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="report-search"><Search className="w-4 h-4 mr-1" /> Consultar</Button>
    {children}
  </CardContent></Card>
);

export const ConversationsReport = () => {
  const navigate = useNavigate();
  const { f, setF, users, params } = useReportFilters();
  const [rows, setRows] = useState(null);
  const search = () => api.get("/reports/conversations", { params }).then(({ data }) => setRows(data)).catch((e) => toast.error(e.response?.data?.detail || "Falha"));
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <FiltersBar f={f} setF={setF} users={users} onSearch={search}>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => downloadCsv("/reports/conversations", { ...params, format: "csv" }, "conversas.csv")} data-testid="report-export-conversations"><Download className="w-4 h-4 mr-1" /> Exportar conversas</Button>
          <Button size="sm" variant="outline" onClick={() => downloadCsv("/reports/messages", params, "mensagens.csv")} data-testid="report-export-messages"><FileText className="w-4 h-4 mr-1" /> Exportar mensagens</Button>
        </div>
      </FiltersBar>
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-conversations-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
            {["Contato", "Setor", "Atendentes", "Mensagens", "Primeira", "Última", "Nota", ""].map((h) => <th key={h} className="p-3 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows?.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Nenhuma conversa no período.</td></tr>}
            {(rows || []).map((r) => (
              <tr key={r.chat_id} className="border-b border-border/60 hover:bg-muted/40" data-testid="report-conversation-row">
                <td className="p-3"><div className="font-semibold">{r.contact_name}</div><div className="text-xs text-muted-foreground mono">+{r.phone}</div></td>
                <td className="p-3">{r.department || "—"}</td>
                <td className="p-3 text-xs">{r.attendants.join(", ") || "—"}</td>
                <td className="p-3 mono text-xs">{r.messages} <span className="text-muted-foreground">({r.inbound}↓ {r.outbound}↑)</span></td>
                <td className="p-3 text-xs">{fmt(r.first_at)}</td>
                <td className="p-3 text-xs">{fmt(r.last_at)}</td>
                <td className="p-3">{r.rating ? <Badge className="bg-amber-100 text-amber-800"><Star className="w-3 h-3 mr-0.5 fill-current" />{r.rating}</Badge> : "—"}</td>
                <td className="p-3 whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/inbox/${r.chat_id}`)} data-testid="report-open-chat">Abrir</Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadCsv("/reports/messages", { ...params, chat_id: r.chat_id }, `conversa_${r.phone}.csv`)} data-testid="report-export-chat"><Download className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
};
